import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import {
  TriplePPagination,
  triplePSDatasource,
} from "../../datasource";
import { Receipt, ReceiptFilters, ReceiptUpdate, RangeSummary } from "../../models";
import { RootState } from "../../app/store";

export interface ReceiptsState {
  getReceipts: APIResult<Receipt[]>;
  receipts: Receipt[];
  updateReceipt: APIResult<void>;
  deleteReceipt: APIResult<string>;
  receiptsSummary: RangeSummary | null;
  getReceiptsSummaryResult: APIResult<RangeSummary>;
}

const initialState: ReceiptsState = {
  getReceipts: emptyAPIResult(),
  receipts: [],
  updateReceipt: emptyAPIResult(),
  deleteReceipt: emptyAPIResult(),
  receiptsSummary: null,
  getReceiptsSummaryResult: emptyAPIResult(),
};

export const getReceipts = createAsyncThunk(
  "receipts/getReceipts",
  async (data: { filters: ReceiptFilters; pagination?: TriplePPagination }) => {
    return triplePSDatasource.getReceipts(data.filters, data.pagination);
  }
);

export const getReceiptsSummary = createAsyncThunk(
  "receipts/getReceiptsSummary",
  async (data: { filters: ReceiptFilters }) => {
    return triplePSDatasource.getReceiptSummary(data.filters);
  }
);

export const updateReceipt = createAsyncThunk(
  "receipts/updateReceipt",
  async (data: { id: string; update: ReceiptUpdate }) => {
    return triplePSDatasource.updateReceipt(data.id, data.update);
  }
);

export const deleteReceipt = createAsyncThunk(
  "receipts/deleteReceipt",
  async (id: string) => {
    return triplePSDatasource.deleteReceipt(id);
  }
);

export const receiptsSlice = createSlice({
  name: "receipts",
  initialState,
  reducers: {
    resetReceipts(state) {
      state.getReceipts = initialState.getReceipts;
      state.receipts = [];
    },
    resetUpdateReceipt(state) {
      state.updateReceipt = initialState.updateReceipt;
    },
    resetDeleteReceipt(state) {
      state.deleteReceipt = initialState.deleteReceipt;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReceipts.pending, (state) => {
        state.getReceipts = loadingAPIResult();
      })
      .addCase(getReceipts.fulfilled, (state, action) => {
        state.getReceipts = successAPIResult(action.payload);
        state.receipts = action.payload;
      })
      .addCase(getReceipts.rejected, (state, action) => {
        state.getReceipts = errorAPIResult(action.error);
      })
      .addCase(getReceiptsSummary.pending, (state) => {
        state.getReceiptsSummaryResult = loadingAPIResult();
      })
      .addCase(getReceiptsSummary.fulfilled, (state, action) => {
        state.getReceiptsSummaryResult = successAPIResult(action.payload);
        state.receiptsSummary = action.payload;
      })
      .addCase(getReceiptsSummary.rejected, (state, action) => {
        state.getReceiptsSummaryResult = errorAPIResult(action.error);
      })
      .addCase(updateReceipt.pending, (state) => {
        state.updateReceipt = loadingAPIResult();
      })
      .addCase(updateReceipt.fulfilled, (state) => {
        state.updateReceipt = successAPIResult(undefined);
      })
      .addCase(updateReceipt.rejected, (state, action) => {
        state.updateReceipt = errorAPIResult(action.error);
      })
      .addCase(deleteReceipt.pending, (state) => {
        state.deleteReceipt = loadingAPIResult();
      })
      .addCase(deleteReceipt.fulfilled, (state, action) => {
        state.deleteReceipt = successAPIResult(action.payload);
        state.receipts = state.receipts.filter((r) => r.id !== action.payload);
      })
      .addCase(deleteReceipt.rejected, (state, action) => {
        state.deleteReceipt = errorAPIResult(action.error);
      });
  },
});

export const { resetReceipts, resetUpdateReceipt, resetDeleteReceipt } =
  receiptsSlice.actions;

export const selectReceipts = (state: RootState) =>
  state.receipts.receipts;
export const selectGetReceiptsResult = (state: RootState) =>
  state.receipts.getReceipts;
export const selectUpdateReceiptResult = (state: RootState) =>
  state.receipts.updateReceipt;
export const selectDeleteReceiptResult = (state: RootState) =>
  state.receipts.deleteReceipt;
export const selectReceiptsSummary = (state: RootState) =>
  state.receipts.receiptsSummary;
export const selectGetReceiptsSummaryResult = (state: RootState) =>
  state.receipts.getReceiptsSummaryResult;

export default receiptsSlice.reducer;
